/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
// Note: The above eslint-disable is needed because this module imports from parent directories.
// This is acceptable here as query-plan is tightly coupled to search-page-relay.
import { useEffect, useRef } from 'react';

import deepEqual from 'deep-equal';
import type { UseQueryLoaderLoadQueryOptions } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
import type { BackendExperiment } from '@atlassian/search-experiment/types';

import { SEARCH_PAGE } from '../../../common/constants/experience';
import type { AllFilterStates } from '../../../common/constants/filters/types';
import type { PrimaryProductKey } from '../../../common/constants/products';
import type { BootstrapVariables, SearchVariables } from '../../../common/constants/relay/types';
import { ResultsStates } from '../../../common/constants/results';
import type {
	ProductKeys,
	SearchQueryStringParams,
} from '../../../common/constants/schemas/query-params';
import type { SearchPreloadDataProvider } from '../../../common/types/search-preload';
import { searchResultsLoadUFOExperienceStart } from '../../../common/ui/ufo/experiences';
import { getApplicationMode } from '../../../common/utils/bootstrap';
import { getSessionIdFromLocalStorage } from '../../../common/utils/events/utils';
import { serveFederatedSlackExperiment } from '../../../common/utils/experiments/experiment-utils';
import { getLeapQueryMemo, isLeapQueryEnabled } from '../../../common/utils/leap-query';
import type { QueryParams } from '../../../common/utils/query-params/types';
import {
	getBootstrapVariables,
	getSearchVariables,
} from '../../../common/utils/relay/get-variables';
import { setQueryUpdatedTime } from '../../../common/utils/relay/query-updated-time';
import { useBootstrapState } from '../../../controllers/store/bootstrap';
import { useSearchState } from '../../../controllers/store/search';
import { generateSearchInputs } from '../../../services/search-query-generator';
import type { rightPanelAsyncQuery$variables } from '../right-panel/__generated__/rightPanelAsyncQuery.graphql';
import type { rightPanelQuery$variables } from '../right-panel/__generated__/rightPanelQuery.graphql';
import type { searchResultsAsyncQuery$variables } from '../search-results/__generated__/searchResultsAsyncQuery.graphql';
import type { searchResultsCqlQuery$variables } from '../search-results/__generated__/searchResultsCqlQuery.graphql';
import type { searchResultsQuery$variables } from '../search-results/__generated__/searchResultsQuery.graphql';
import { getMaxResultsToShow } from '../utils/get-max-results-to-show';
import { getRelayFetchOptions } from '../utils/get-relay-fetch-options';
import { getSearchPageMode, type SearchPageMode } from '../utils/get-search-page-mode';
import {
	type ReportPreloadQueryVarsDiff,
	usePreloadQueryVarsDiffReporter,
} from '../utils/preload-query-vars-diff';
import {
	type FullPageSearchVitalStatsAPI,
	useFullPageSearchVitalStats,
} from '../vital-stats-context';

type VoidFunc = () => void;

type Loader<TVariables> = (variables: TVariables, options?: UseQueryLoaderLoadQueryOptions) => void;

export type UseQueryPlanArgs = {
	// Query loaders / disposers
	loadSearchResultsQuery: Loader<searchResultsQuery$variables>;
	disposeSearchResultsQuery: VoidFunc;
	loadSearchResultsAsyncQuery: Loader<searchResultsAsyncQuery$variables>;
	disposeSearchResultsAsyncQuery: VoidFunc;
	loadRightPanelQuery?: Loader<rightPanelQuery$variables>;
	disposeRightPanelQuery?: VoidFunc;
	loadRightPanelAsyncQuery?: Loader<rightPanelAsyncQuery$variables>;
	disposeRightPanelAsyncQuery?: VoidFunc;
	loadSearchResultsCQLQuery: Loader<searchResultsCqlQuery$variables>;
	disposeSearchResultsCQLQuery: VoidFunc;

	// Local state mirrors
	setBootstrap: (bootstrap: BootstrapVariables) => void;
	setSearch: (search: SearchVariables) => void;

	// UI/metrics side effects
	setResultsState: (state: ResultsStates) => void;
	resetErrorBoundary?: VoidFunc;
	bodyRef: React.RefObject<HTMLDivElement>;
	setMaxResultsToShow: React.Dispatch<React.SetStateAction<number | undefined>>;
	backendExperimentRef: React.MutableRefObject<BackendExperiment | undefined>;
	preloadDataProvider?: SearchPreloadDataProvider;

	// Leap query inputs (passed from the UI layer to avoid subscribing to more stores here).
	isAdminHubAIEnabled?: boolean;
	isRovoLLMEnabled?: boolean;
	logError: (error: Error) => void | Promise<void>;

	// Search request dependencies derived from query params (independent of search store)
	cql: string | undefined;
	searchQuery: string;
	filters: Record<string, AllFilterStates | undefined>;
	selectedProducts: ProductKeys[];

	queryParams: QueryParams;
};

export type UseQueryPlanResult = {
	// The orchestrator-derived mode used by rendering to decide Landing vs Results.
	mode: SearchPageMode;
	bootstrap: BootstrapVariables;
	search: SearchVariables;
};

type QueryPlan = {
	mode: SearchPageMode;
	bootstrap: BootstrapVariables;
	search: SearchVariables;
	cql: SearchQueryStringParams['cql'] | undefined;
	selectedProducts: ProductKeys[];
	queryParams: QueryParams;
};

type ExecuteQueryPlanArgs = {
	plan: QueryPlan;
	loadSearchResultsQuery: Loader<searchResultsQuery$variables>;
	loadSearchResultsAsyncQuery: Loader<searchResultsAsyncQuery$variables>;
	loadRightPanelQuery?: Loader<rightPanelQuery$variables>;
	loadRightPanelAsyncQuery?: Loader<rightPanelAsyncQuery$variables>;
	loadSearchResultsCQLQuery: Loader<searchResultsCqlQuery$variables>;
	disposeSearchResultsQuery: VoidFunc;
	disposeSearchResultsAsyncQuery: VoidFunc;
	disposeSearchResultsCQLQuery: VoidFunc;
	disposeRightPanelQuery?: VoidFunc;
	disposeRightPanelAsyncQuery?: VoidFunc;
	setBootstrap: (bootstrap: BootstrapVariables) => void;
	setSearch: (search: SearchVariables) => void;
	setResultsState: (state: ResultsStates) => void;
	resetErrorBoundary?: VoidFunc;
	setMaxResultsToShow: React.Dispatch<React.SetStateAction<number | undefined>>;
	bodyRef: React.RefObject<HTMLDivElement>;
	backendExperimentRef: React.MutableRefObject<BackendExperiment | undefined>;
	reportPreloadQueryVarsDiff: ReportPreloadQueryVarsDiff;
	// VitalStats API from context
	vitalStats: FullPageSearchVitalStatsAPI;
	isAdminHubAIEnabled: boolean;
	isRovoLLMEnabled: boolean;
	logError: (error: Error) => void | Promise<void>;
	preloadDataProvider?: SearchPreloadDataProvider;
};

/**
 * Common setup for search modes (RESULTS and CQL_RESULTS).
 * Handles task ID generation, analytics, error boundary reset, and scroll behavior.
 */
function setupSearchExecution({
	onSearchStart,
	resetErrorBoundary,
	setQueryUpdatedTime,
	bodyRef,
	setMaxResultsToShow,
	cacheAvailable,
	cacheExpected,
	isNetworkMode,
}: {
	onSearchStart: (options: {
		cacheAvailable: boolean;
		cacheExpected: boolean;
		isNetworkMode: boolean;
	}) => string;
	resetErrorBoundary?: VoidFunc;
	setQueryUpdatedTime: () => void;
	bodyRef: React.RefObject<HTMLDivElement>;
	setMaxResultsToShow: React.Dispatch<React.SetStateAction<number | undefined>>;
	cacheAvailable: boolean;
	cacheExpected: boolean;
	isNetworkMode: boolean;
}): void {
	// VitalStats: fires UX taskStart + Network taskStart (and superseded abort)
	// This also sets the UX task ID ref internally via context
	onSearchStart({ cacheAvailable, cacheExpected, isNetworkMode });

	// Reset error boundary and query timestamp
	resetErrorBoundary?.();
	setQueryUpdatedTime();

	// Scroll to top and update max results
	bodyRef.current?.scrollTo?.(0, 0);
	setMaxResultsToShow(getMaxResultsToShow(window.innerHeight));
}

/**
 * Load LeapQuery as a non-CQL enhancement (errors are non-fatal and should not block results).
 */
function loadLeapQuery({
	bootstrap,
	searchQuery,
	isAdminHubAIEnabled,
	isRovoLLMEnabled,
	logError,
}: {
	bootstrap: Pick<BootstrapVariables, 'primaryProduct' | 'isRovoEnabled'>;
	searchQuery: string;
	isAdminHubAIEnabled: boolean;
	isRovoLLMEnabled: boolean;
	logError: (error: Error) => void | Promise<void>;
}): void {
	const { primaryProduct, isRovoEnabled } = bootstrap;
	if (
		primaryProduct &&
		searchQuery &&
		isLeapQueryEnabled(getApplicationMode({ isRovoEnabled }), isAdminHubAIEnabled, isRovoLLMEnabled)
	) {
		// Loading state handled in the LeapQuery component.
		getLeapQueryMemo({ query: searchQuery, product: primaryProduct }).catch((e: Error) => {
			logError(e);
		});
	}
}

/**
 * Executes the query plan: loads/disposes queries, fires analytics, and updates state.
 * This is the single point where we orchestrate all query loading based on the derived mode.
 */
function executeQueryPlan({
	plan,
	loadSearchResultsQuery,
	loadSearchResultsAsyncQuery,
	loadRightPanelQuery,
	loadRightPanelAsyncQuery,
	loadSearchResultsCQLQuery,
	disposeSearchResultsQuery,
	disposeSearchResultsAsyncQuery,
	disposeSearchResultsCQLQuery,
	disposeRightPanelQuery,
	disposeRightPanelAsyncQuery,
	setBootstrap,
	setSearch,
	setResultsState,
	resetErrorBoundary,
	setMaxResultsToShow,
	bodyRef,
	backendExperimentRef,
	reportPreloadQueryVarsDiff,
	vitalStats,
	isAdminHubAIEnabled,
	isRovoLLMEnabled,
	logError,
	preloadDataProvider,
}: ExecuteQueryPlanArgs): void {
	const { mode, bootstrap, search, cql, selectedProducts, queryParams } = plan;

	const relayFetchOptions = getRelayFetchOptions(bootstrap);

	// RESULTS: load results + counts + leapQuery; ensure CQL ref is disposed.
	if (mode === 'RESULTS') {
		disposeSearchResultsCQLQuery();

		// Dispose the query path we're NOT using
		if (fg('search_async_ssr')) {
			disposeSearchResultsQuery();
		} else {
			disposeSearchResultsAsyncQuery();
		}

		const { searchResultsQueryVariables, rightPanelQueryVariables, backendExperiment } =
			generateSearchInputs({
				backendExperimentOverrides: bootstrap.overrides?.backendExperiments || [],
				cloudId: bootstrap.cloudId || '',
				user: bootstrap.user,
				primaryProduct: bootstrap.primaryProduct as PrimaryProductKey, // Guaranteed by bootstrap being initialised
				experimentLayers: bootstrap.experimentLayers,
				experience: SEARCH_PAGE,
				thirdPartyConfigs: bootstrap.thirdPartyConfigs,
				products: bootstrap.products,
				productEdition: bootstrap.productEdition || {},
				productDataRegions: bootstrap.productDataRegions || {},
				isJiraIssueIndexed: bootstrap.isJiraIssueIndexed,
				sessionId: getSessionIdFromLocalStorage(),
				searchSessionId: bootstrap.searchSessionId,
				queryParams,
				jsmIncidentFilterIds: search.jsmIncidentFilterIds, // This is the last argument that comes from the search store
				onError: logError,
			});

		serveFederatedSlackExperiment(
			undefined,
			selectedProducts,
			{ fireExperimentExposure: true },
			bootstrap.thirdPartyConfigs,
		);

		if (backendExperiment) {
			backendExperimentRef.current = backendExperiment;
		}

		searchResultsLoadUFOExperienceStart();

		// Used for smart answers to track results state.
		setResultsState(ResultsStates.IS_LOADING);

		// Note: cacheAvailable is always false because Relay's store-or-network policy
		// always makes network requests. The label is kept for future use if we change fetch policy.
		const cacheAvailable = false;

		// A cache hit is expected when the preload data provider has search query variables
		// from a successful preload (e.g. SSR). This is only true for the first search in
		// a session because clearPreloadedSearch is called after the first results body renders.
		const cacheExpected = Boolean(
			preloadDataProvider?.getPreloadedSearch({ cloudId: bootstrap.cloudId || '' })?.search
				?.queryVariables,
		);

		setupSearchExecution({
			onSearchStart: vitalStats.onSearchStart,
			resetErrorBoundary,
			setQueryUpdatedTime,
			bodyRef,
			setMaxResultsToShow,
			cacheAvailable,
			cacheExpected,
			isNetworkMode: true, // RESULTS mode uses network VitalStats
		});

		if (fg('search_async_ssr')) {
			loadSearchResultsAsyncQuery(searchResultsQueryVariables, relayFetchOptions);
			disposeRightPanelQuery?.();
			loadRightPanelAsyncQuery?.(rightPanelQueryVariables, relayFetchOptions);
		} else {
			loadSearchResultsQuery(searchResultsQueryVariables, relayFetchOptions);
			disposeRightPanelAsyncQuery?.();
			loadRightPanelQuery?.(rightPanelQueryVariables, relayFetchOptions);
		}
		// Load LeapQuery after kicking off the core queries.
		loadLeapQuery({
			bootstrap,
			searchQuery: search.searchQuery,
			isAdminHubAIEnabled,
			isRovoLLMEnabled,
			logError,
		});

		if (fg('search_async_ssr')) {
			reportPreloadQueryVarsDiff({
				cloudId: bootstrap.cloudId,
				searchResultsQueryVariables,
				rightPanelQueryVariables,
			});
		}
	}

	// CQL_RESULTS: load CQL results; ensure non-CQL refs are disposed.
	if (mode === 'CQL_RESULTS') {
		disposeRightPanelQuery?.();
		disposeRightPanelAsyncQuery?.();
		disposeSearchResultsQuery();
		disposeSearchResultsAsyncQuery();

		setupSearchExecution({
			onSearchStart: vitalStats.onSearchStart,
			resetErrorBoundary,
			setQueryUpdatedTime,
			bodyRef,
			setMaxResultsToShow,
			cacheAvailable: false, // CQL mode doesn't track cache
			cacheExpected: false, // CQL mode doesn't use preloading
			isNetworkMode: false, // CQL_RESULTS mode is out of scope for network VitalStats
		});
		// `mode` guarantees cql exists here.
		loadSearchResultsCQLQuery({ cql: cql as string, first: 30, after: null }, relayFetchOptions);
	}

	// LANDING: invalidate all query refs so stale results can't render/suspend.
	if (mode === 'LANDING') {
		disposeSearchResultsQuery();
		disposeSearchResultsAsyncQuery();
		disposeRightPanelQuery?.();
		disposeRightPanelAsyncQuery?.();
		disposeSearchResultsCQLQuery();
	}

	// Keep local mirrors in sync for rendering.
	setSearch(search);
	setBootstrap(bootstrap);
}

/**
 * This query plan hook handles the lifecycle of search requests being fired
 * 	1. Derive mode based on bootstrap and search variables
 * 	2. Load/dispose Relay queries based on the mode
 * 	3. Start VitalStats and update local mirrors
 *
 * As part of M3 of the async query work, this hook will be accepting data that is part
 * of the search store as props. When these props are defined, they will be used as the
 * source of truth for search requests. Indeed, this data will essentially be a duplicate of the
 * data coming from useSearchState, however, the difference is that they will not be sourced
 * from the search store. Eventually, we will no longer need variables from the search store
 * to make a search request. It is also intention that this is not wrapped as an object called
 * 'search' as we want to move away from the idea of a search store. Rather, they are just piece
 * of data that are prop drilled. This data will typically be derived from the query parameters,
 * or fetched from the server.
 *
 * To make this clear, the following search store variables are passed as props:
 * - cql
 */
export function useQueryPlan({
	loadSearchResultsQuery,
	disposeSearchResultsQuery,
	loadSearchResultsAsyncQuery,
	disposeSearchResultsAsyncQuery,
	loadRightPanelQuery,
	disposeRightPanelQuery,
	loadRightPanelAsyncQuery,
	disposeRightPanelAsyncQuery,
	loadSearchResultsCQLQuery,
	disposeSearchResultsCQLQuery,
	setBootstrap,
	setSearch,
	setResultsState,
	resetErrorBoundary,
	bodyRef,
	setMaxResultsToShow,
	backendExperimentRef,
	preloadDataProvider,
	isAdminHubAIEnabled = false,
	isRovoLLMEnabled = false,
	logError,
	cql,
	searchQuery,
	filters,
	selectedProducts,
	queryParams,
}: UseQueryPlanArgs): UseQueryPlanResult {
	const vitalStats = useFullPageSearchVitalStats();

	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const search: SearchVariables = useSearchState(getSearchVariables);
	const bootstrap: BootstrapVariables = useBootstrapState(getBootstrapVariables);
	const reportPreloadQueryVarsDiff = usePreloadQueryVarsDiffReporter(preloadDataProvider);

	// Available products are sourced from bootstrap store/context, not local search mirror
	const availableProducts = bootstrap.products;

	// Bootstrap is ready when there are available products
	const bootstrapReady = availableProducts.length > 0;

	// Filters are considered selected when there is a selected product or any filter has a selection
	const hasAnyFilterSelected =
		Boolean(selectedProducts[0]) || Object.values(filters).some((filter) => filter?.hasSelection);

	// There should only ever be one selected product
	const selectedProduct = selectedProducts[0];

	const mode = getSearchPageMode({
		bootstrapReady,
		availableProducts,
		selectedProduct,
		searchQuery,
		hasAnyFilterSelected,
		cql,
	});

	const previousPlanRef = useRef<QueryPlan>();

	// Refs for non-reactive dependencies: always created to satisfy React's
	// rules of hooks (hooks must be called in the same order every render).
	// When the feature flag is ON, the useEffect reads from these refs instead
	// of using the values directly, so changes to store objects and callbacks
	// don't trigger unnecessary re-executions.
	const bootstrapRef = useRef(bootstrap);
	bootstrapRef.current = bootstrap;

	const searchRef = useRef(search);
	searchRef.current = search;

	const loadSearchResultsQueryRef = useRef(loadSearchResultsQuery);
	loadSearchResultsQueryRef.current = loadSearchResultsQuery;

	const loadSearchResultsAsyncQueryRef = useRef(loadSearchResultsAsyncQuery);
	loadSearchResultsAsyncQueryRef.current = loadSearchResultsAsyncQuery;

	const loadRightPanelQueryRef = useRef(loadRightPanelQuery);
	loadRightPanelQueryRef.current = loadRightPanelQuery ?? undefined;

	const loadRightPanelAsyncQueryRef = useRef(loadRightPanelAsyncQuery);
	loadRightPanelAsyncQueryRef.current = loadRightPanelAsyncQuery ?? undefined;

	const loadSearchResultsCQLQueryRef = useRef(loadSearchResultsCQLQuery);
	loadSearchResultsCQLQueryRef.current = loadSearchResultsCQLQuery;

	const disposeSearchResultsQueryRef = useRef(disposeSearchResultsQuery);
	disposeSearchResultsQueryRef.current = disposeSearchResultsQuery;

	const disposeSearchResultsAsyncQueryRef = useRef(disposeSearchResultsAsyncQuery);
	disposeSearchResultsAsyncQueryRef.current = disposeSearchResultsAsyncQuery;

	const disposeSearchResultsCQLQueryRef = useRef(disposeSearchResultsCQLQuery);
	disposeSearchResultsCQLQueryRef.current = disposeSearchResultsCQLQuery;

	const disposeRightPanelQueryRef = useRef(disposeRightPanelQuery);
	disposeRightPanelQueryRef.current = disposeRightPanelQuery ?? undefined;

	const disposeRightPanelAsyncQueryRef = useRef(disposeRightPanelAsyncQuery);
	disposeRightPanelAsyncQueryRef.current = disposeRightPanelAsyncQuery ?? undefined;

	const resetErrorBoundaryRef = useRef(resetErrorBoundary);
	resetErrorBoundaryRef.current = resetErrorBoundary;

	const vitalStatsRef = useRef(vitalStats);
	vitalStatsRef.current = vitalStats;

	const logErrorRef = useRef(logError);
	logErrorRef.current = logError;

	const reportPreloadQueryVarsDiffRef = useRef(reportPreloadQueryVarsDiff);
	reportPreloadQueryVarsDiffRef.current = reportPreloadQueryVarsDiff;

	useEffect(() => {
		if (!bootstrapReady) {
			return;
		}

		const useStableDeps = fg('rovo-search-vitalstats-stable-query-plan-deps');

		// The deepEqual comparison only checks the search identity fields. bootstrap
		// and search are excluded because they contain non-search-relevant fields
		// (filter options, third-party configs, etc.) that change independently.
		// The search-relevant fields from bootstrap/search are already extracted as
		// individual variables (searchQuery, filters, selectedProducts, etc.) and
		// compared directly.
		const nextPlan: QueryPlan = {
			mode,
			bootstrap: useStableDeps ? ({} as typeof bootstrap) : bootstrap,
			search: useStableDeps ? ({} as typeof search) : search,
			cql,
			selectedProducts,
			queryParams,
		};

		if (deepEqual(previousPlanRef.current, nextPlan)) {
			return;
		}

		if (
			previousPlanRef.current &&
			preloadDataProvider &&
			bootstrapRef.current.cloudId &&
			fg('search_async_ssr')
		) {
			// Once the hydrated plan transitions to the first client-driven plan, retire the SSR preload
			// context so subsequent searches do not report as initial async-SSR loads.
			preloadDataProvider.clearPreloadedSearch({ cloudId: bootstrapRef.current.cloudId });
		}

		previousPlanRef.current = nextPlan;

		executeQueryPlan({
			plan: { ...nextPlan, bootstrap: bootstrapRef.current, search: searchRef.current },
			loadSearchResultsQuery: loadSearchResultsQueryRef.current,
			loadSearchResultsAsyncQuery: loadSearchResultsAsyncQueryRef.current,
			loadRightPanelQuery: loadRightPanelQueryRef.current,
			loadRightPanelAsyncQuery: loadRightPanelAsyncQueryRef.current,
			loadSearchResultsCQLQuery: loadSearchResultsCQLQueryRef.current,
			disposeSearchResultsQuery: disposeSearchResultsQueryRef.current,
			disposeSearchResultsAsyncQuery: disposeSearchResultsAsyncQueryRef.current,
			disposeSearchResultsCQLQuery: disposeSearchResultsCQLQueryRef.current,
			disposeRightPanelQuery: disposeRightPanelQueryRef.current,
			disposeRightPanelAsyncQuery: disposeRightPanelAsyncQueryRef.current,
			setBootstrap,
			setSearch,
			setResultsState,
			resetErrorBoundary: resetErrorBoundaryRef.current,
			setMaxResultsToShow,
			bodyRef,
			backendExperimentRef,
			reportPreloadQueryVarsDiff: reportPreloadQueryVarsDiffRef.current,
			vitalStats: vitalStatsRef.current,
			isAdminHubAIEnabled,
			isRovoLLMEnabled,
			logError: logErrorRef.current,
			preloadDataProvider,
		});
	}, [
		bootstrap,
		search,
		bootstrapReady,
		mode,
		loadSearchResultsQuery,
		loadSearchResultsAsyncQuery,
		loadRightPanelQuery,
		loadRightPanelAsyncQuery,
		loadSearchResultsCQLQuery,
		disposeSearchResultsQuery,
		disposeSearchResultsAsyncQuery,
		disposeSearchResultsCQLQuery,
		disposeRightPanelQuery,
		disposeRightPanelAsyncQuery,
		setBootstrap,
		setSearch,
		setResultsState,
		resetErrorBoundary,
		setMaxResultsToShow,
		bodyRef,
		backendExperimentRef,
		vitalStats,
		isAdminHubAIEnabled,
		isRovoLLMEnabled,
		logError,
		cql,
		searchQuery,
		filters,
		selectedProducts,
		queryParams,
		preloadDataProvider,
	]);

	return { mode, bootstrap, search };
}

/**
 * Leaf orchestrator component: subscribes to RSS stores and runs query orchestration without rendering UI.
 * This prevents frequent store updates from re-rendering the heavy SearchPage UI tree.
 */
export function SearchPageQueryOrchestrator(args: UseQueryPlanArgs): null {
	useQueryPlan(args);
	return null;
}
